import { IconPlus, IconTrash } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useMemo } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import classNames from 'classnames';
import {
    NSTable,
    NSCellInput,
    NSSelect,
} from 'bricks';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import RenderIf from 'components/RenderIf/RenderIf';
import {
    CALCULATION_OPTIONS, OPERATIONS_INCOME_WS_HANDLERS, tableValueClassName, AdjustmentType,
    CASH_FLOW_TOOLTIP_MESSAGE, WattsType,
} from 'views/ProFormaTable/constants';
import { IncomeCalculationType } from 'constants/proForma';
import { IncomeAdjustments } from 'views/ProFormaTable/types';
import { useProFormaSocketContext } from '../../../../socketContext/ProFormaSocketProvider';
import { ColumnKeys, COLUMN_KEYS_BY_MODEL_ID } from './constants';
import './IncomeAdjustmentsTable.scss';
import { isAllApartmentModels, isApartmentWithRetailModel, isModelWithLeasingCostReserves, isStudentHousingModel } from 'helpers/proForma';

export interface IIncomeAdjustmentsTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
    showEgrBreakdown?: boolean;
    isRetailView?: boolean;
}

const IncomeAdjustmentsTable = ({
    operationBeginIndex, baseCashFlowColumns, showEgrBreakdown = false, isRetailView = false,
}: IIncomeAdjustmentsTableProps) => {
    const {
        incomeSummary, handleCreateOperationsRow, handleUpdateOperationsRow, handleDeleteOperationsRow,
        showCents, formatDisplayValue, operationsCashFlow, proForma, operatingInfo, wattsType,
    } = useProFormaSocketContext();

    const useRetailEgr = isApartmentWithRetailModel(proForma.proFormaModelId) && !showEgrBreakdown;
    const egrSummary = useRetailEgr ? incomeSummary.retailEffectiveGrossRevenue : incomeSummary.effectiveGrossRevenue;
    const isIncomeCalculationTypeEstimate = operatingInfo?.incomeCalculationType === IncomeCalculationType.ESTIMATE;

    const areaType = isAllApartmentModels(proForma.proFormaModelId) ||
        isStudentHousingModel(proForma.proFormaModelId) ||
        !isIncomeCalculationTypeEstimate ||
        isModelWithLeasingCostReserves(proForma.proFormaModelId) ? 'RSF' : 'SF';

    const adjHandlers = isRetailView ? OPERATIONS_INCOME_WS_HANDLERS.RETAIL_INCOME_ADJUSTMENT : OPERATIONS_INCOME_WS_HANDLERS.INCOME_ADJUSTMENT;
    const adjData = isRetailView ? (incomeSummary.retailIncomeAdjustments || []) : (incomeSummary.incomeAdjustments || []);
    const adjCashFlowKey = isRetailView ? 'retailIncomeAdjustments' : 'incomeAdjustments';

    const calculationTypeOptions = useMemo(() => {
        if (!incomeSummary?.incomeAdjustmentCalculationTypes) return CALCULATION_OPTIONS;
        return CALCULATION_OPTIONS.filter(option => incomeSummary.incomeAdjustmentCalculationTypes?.includes(option.value));
    }, [incomeSummary?.incomeAdjustmentCalculationTypes]);

    const handleUpdateIncomeAdjustments = (id: number, field: string) => handleUpdateOperationsRow(
        adjHandlers.UPDATE, id, field,
    );

    const incomeAdjustmentsCashFlowColumns: ColumnDef<IncomeAdjustments>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `income-adjustment-period-${column.id}`,
        accessorFn: (row, rowIndex: number) => {
            const cfData = operationsCashFlow?.[operationBeginIndex]?.[adjCashFlowKey] as any;
            const cfRow = (operationsCashFlow?.[cashFlowIndex]?.[adjCashFlowKey] as any)?.[rowIndex];
            return typeof cfData?.[rowIndex]?.value === 'number' ? (cfRow?.value || 0) : null;
        },
        footer: () => (
            <RenderIf isTrue={operationBeginIndex}>
                {showEgrBreakdown ? (
                    <div className="text-dark IncomeAdjustmentsTable__egr-breakdown">
                        <div>{formatDisplayValue(operationsCashFlow?.[cashFlowIndex]?.apartmentEffectiveGrossRevenue || 0, showCents, 'currency')}</div>
                        <div>{formatDisplayValue(operationsCashFlow?.[cashFlowIndex]?.retailEffectiveGrossRevenue || 0, showCents, 'currency')}</div>
                        <div>{formatDisplayValue(operationsCashFlow?.[cashFlowIndex]?.effectiveGrossRevenue || 0, showCents, 'currency')}</div>
                    </div>
                ) : (
                    <span className="text-dark">
                        {formatDisplayValue(
                            operationsCashFlow?.[cashFlowIndex]?.[useRetailEgr ? 'retailEffectiveGrossRevenue' : 'effectiveGrossRevenue'] || 0,
                            showCents, 'currency',
                        )}
                    </span>
                )}
            </RenderIf>
        ),
    }));

    const COLUMNS_BY_KEY: { [key in ColumnKeys]: ColumnDef<IncomeAdjustments> } = {
        [ColumnKeys.PERCENT_OF_INCOME]: {
            accessorKey: 'percentOfIncome',
            id: 'percentOfIncome',
            header: () => <span>% of income</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    numberType="percentage"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateIncomeAdjustments(Number(row.original.id), 'percentOfIncome');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter percentage"
                />
            ),
        },
        [ColumnKeys.PRICE_PER_SF]: {
            accessorKey: 'pricePerSf',
            id: 'pricePerSf',
            header: () => <span>{`$/${areaType}/mo.`}</span>,
            cell: ({ row, getValue }) => (
                <span className={tableValueClassName(getValue())}>
                    {getValue() === null ? '—' : formatDisplayValue(getValue(), true, 'currency')}
                </span>
            ),
            footer: () => (
                showEgrBreakdown ? (
                    <div className="IncomeAdjustmentsTable__egr-breakdown">
                        <div>{formatDisplayValue(incomeSummary.apartmentEffectiveGrossRevenue?.totalPricePerSf || 0, true, 'currency')}</div>
                        <div>{formatDisplayValue(incomeSummary.retailEffectiveGrossRevenue?.totalPricePerSf || 0, true, 'currency')}</div>
                        <div>{formatDisplayValue(incomeSummary.effectiveGrossRevenue?.totalPricePerSf || 0, true, 'currency')}</div>
                    </div>
                ) : (
                    <span>{formatDisplayValue(egrSummary?.totalPricePerSf || 0, true, 'currency')}</span>
                )
            ),
        },
        [ColumnKeys.PRICE_PER_UNIT]: {
            accessorKey: 'pricePerUnit',
            id: 'pricePerUnit',
            header: () => <span>$/unit/mo.</span>,
            cell: ({ row, getValue }) => (
                <span className={tableValueClassName(getValue())}>
                    {getValue() === null ? '—' : formatDisplayValue(getValue(), true, 'currency')}
                </span>
            ),
            footer: () => (
                showEgrBreakdown ? (
                    <div className="IncomeAdjustmentsTable__egr-breakdown">
                        <div>{formatDisplayValue(incomeSummary.apartmentEffectiveGrossRevenue?.totalPricePerUnit || 0, true, 'currency')}</div>
                        <div>&nbsp;</div>
                        <div>&nbsp;</div>
                        {/* Hide Retail/Total EGR floor/suite type metrics as can't add to Apt Unit type metrics since different data types */}
                        {/* <div>{formatDisplayValue(incomeSummary.retailEffectiveGrossRevenue?.totalPricePerFloorOrSuite || 0, true, 'currency')}</div>
                        <div>{formatDisplayValue(incomeSummary.effectiveGrossRevenue?.totalPricePerUnit || 0, true, 'currency')}</div> */}
                    </div>
                ) : (
                    <span>{formatDisplayValue(egrSummary?.totalPricePerUnit || 0, true, 'currency')}</span>
                )
            ),
        },
        [ColumnKeys.PRICE_PER_BED]: {
            accessorKey: 'pricePerBed',
            id: 'pricePerBed',
            header: () => <span>$/bed/mo.</span>,
            cell: ({ row, getValue }) => (
                <span className={tableValueClassName(getValue())}>
                    {getValue() === null ? '—' : formatDisplayValue(getValue(), true, 'currency')}
                </span>
            ),
            footer: () => (
                <span>{formatDisplayValue(egrSummary?.totalPricePerBed || 0, true, 'currency')}</span>
            ),
        },
        [ColumnKeys.PRICE_PER_RACK_PER_YEAR]: {
            accessorKey: 'pricePerRackPerYear',
            id: 'pricePerRackPerYear',
            header: () => <span>$/rack/year</span>,
            cell: ({ row, getValue }) => (
                <span className={tableValueClassName(getValue())}>
                    {getValue() === null ? '—' : formatDisplayValue(getValue(), true, 'currency')}
                </span>
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.effectiveGrossRevenue?.totalPricePerRackPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.PRICE_PER_KW_PER_YEAR]: wattsType === WattsType.MEGAWATTS ? {
            accessorKey: 'pricePerMwPerYear',
            id: 'pricePerMwPerYear',
            header: () => <span>$/MW/year</span>,
            cell: ({ row, getValue }) => (
                <span className={tableValueClassName(getValue())}>
                    {getValue() === null ? '—' : formatDisplayValue(getValue(), true, 'currency')}
                </span>
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.effectiveGrossRevenue?.totalPricePerMwPerYear || 0, true, 'currency')}</span>,
        } : {
            accessorKey: 'pricePerKwPerYear',
            id: 'pricePerKwPerYear',
            header: () => <span>$/kW/year</span>,
            cell: ({ row, getValue }) => (
                <span className={tableValueClassName(getValue())}>
                    {getValue() === null ? '—' : formatDisplayValue(getValue(), true, 'currency')}
                </span>
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.effectiveGrossRevenue?.totalPricePerKwPerYear || 0, true, 'currency')}</span>,
        },
    };

    const adjMiddleColumns = isRetailView
        ? [ColumnKeys.PERCENT_OF_INCOME, ColumnKeys.PRICE_PER_SF]
        : COLUMN_KEYS_BY_MODEL_ID[proForma.proFormaModelId];

    const incomeAdjustmentsColumns: ColumnDef<any>[] = [
        {
            accessorKey: 'name',
            id: 'name',
            header: () => (
                <div className="d-flex align-items-center justify-content-between w-100 px-1">
                    <span>Income adjustments</span>
                    <NSIcon
                        id="plus-icon"
                        icon={IconPlus}
                        className="ml-2 text-muted cursor--pointer"
                        onClick={() => handleCreateOperationsRow(adjHandlers.CREATE)}
                    />
                </div>
            ),
            cell: ({ row, getValue }) => (
                <div className="d-flex align-items-center justify-content-between w-100 px-1">
                    <div className="w-100 pb-1">
                        <div>
                            <NSCellInput
                                type="text"
                                value={getValue()}
                                handleUpdateValue={updatedValue => {
                                    const updateFn = handleUpdateIncomeAdjustments(Number(row.original.id), 'name');
                                    if (typeof updateFn === 'function') {
                                        updateFn({
                                            target: { value: updatedValue },
                                        } as React.ChangeEvent<HTMLInputElement>);
                                    }
                                }}
                                placeholder="Enter name"
                                disabled={row.original.adjustmentType !== AdjustmentType.OTHER}
                                labelClassName={row.original.adjustmentType !== AdjustmentType.OTHER ? 'text-dark' : ''}
                            />
                        </div>
                        <div>
                            <NSSelect
                                name={`income-adjustment-calculation-type-select-${row.id}`}
                                options={calculationTypeOptions}
                                value={calculationTypeOptions.find(option => option.value === row.original.calculationType)}
                                onChange={option => {
                                    if (option) {
                                        const updateFn = handleUpdateIncomeAdjustments(Number(row.original.id), 'calculationType');
                                        if (typeof updateFn === 'function') {
                                            updateFn({
                                                target: { value: option.value },
                                            } as React.ChangeEvent<HTMLInputElement>);
                                        }
                                    }
                                }}
                                selectedItemPrefix="From: "
                                isClearable={false}
                                menuPosition="fixed"
                                menuPortalTarget={document.body}
                                styles={{
                                    ...customStyles,
                                    control: (provided: any) => ({
                                        ...provided,
                                        backgroundColor: '#4F5B69',
                                        marginTop: '4px',
                                        border: 'none',
                                        minHeight: '28px',
                                    }),
                                }}
                            />
                        </div>
                    </div>
                    <RenderIf isTrue={row.original.adjustmentType === AdjustmentType.OTHER}>
                        <NSIcon
                            id="trash-icon"
                            icon={IconTrash}
                            className="ml-1 text-dark cursor--pointer"
                            onClick={() => handleDeleteOperationsRow(adjHandlers.DELETE, Number(row.original.id))}
                        />
                    </RenderIf>
                </div>
            ),
            footer: () => (
                showEgrBreakdown ? (
                    <div className="IncomeAdjustmentsTable__egr-breakdown">
                        <div className="px-1">Apartment effective gross revenue</div>
                        <div className="px-1">Retail effective gross revenue</div>
                        <div className="px-1">Total effective gross revenue</div>
                    </div>
                ) : (
                    <span className="px-1">Effective gross revenue</span>
                )
            ),
        },
        ...adjMiddleColumns.map(key => COLUMNS_BY_KEY[key]),
        {
            accessorKey: 'amountPerYear',
            id: 'amountPerYear',
            header: () => <span className="pr-1">Amount/year</span>,
            cell: ({ row, getValue }) => (
                <span className={`${tableValueClassName(getValue())} pr-1`}>
                    {getValue() === null ? '—' : formatDisplayValue(getValue(), showCents, 'currency')}
                </span>
            ),
            footer: () => (
                showEgrBreakdown ? (
                    <div className="pr-1 IncomeAdjustmentsTable__egr-breakdown">
                        <div>{formatDisplayValue(incomeSummary.apartmentEffectiveGrossRevenue?.totalAmountPerYear || 0, showCents, 'currency')}</div>
                        <div>{formatDisplayValue(incomeSummary.retailEffectiveGrossRevenue?.totalAmountPerYear || 0, showCents, 'currency')}</div>
                        <div>{formatDisplayValue(incomeSummary.effectiveGrossRevenue?.totalAmountPerYear || 0, showCents, 'currency')}</div>
                    </div>
                ) : (
                    <span className="pr-1">
                        {formatDisplayValue(egrSummary?.totalAmountPerYear || 0, showCents, 'currency')}
                    </span>
                )
            ),
        },
        ...incomeAdjustmentsCashFlowColumns,
    ];

    const table = useReactTable({
        data: adjData,
        columns: incomeAdjustmentsColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <NSTable className={`NSTable IncomeAdjustmentsTable--${isRetailView ? 'retail' : proForma.proFormaModelName.replace(/ /g, '-')} mb-1`}>
            <thead className="NSTable__thead">
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id} className="NSTable__thead__tr">
                        {headerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__thead__tr__th p-1">
                                {header.isPlaceholder ? null : (
                                    <div>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody
                className={classNames('NSTable__tbody', {
                    'NSTable__tbody--pro-forma-placeholder': !table.getRowModel().rows.length,
                })}
            >
                <RenderIf isTrue={table.getRowModel().rows.length}>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id} className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id} className="NSTable__tbody__tr__td p-1">
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext(),
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </RenderIf>
                <RenderIf isTrue={!table.getRowModel().rows.length}>
                    <tr className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                        <td colSpan={adjMiddleColumns.length + 2} className="NSTable__tbody__tr__td text-center text-muted py-4">
                            <div className="text-dark">Your added income will appear here.</div>
                            <div className="small">
                                Click the
                                {' '}
                                <span className="font-weight-bold text-dark">[+]</span>
                                {' '}
                                symbol in the
                                {' '}
                                <span className="font-weight-bold text-dark">Income adjustments</span>
                                {' '}
                                header to add your first income adjustment.
                            </div>
                        </td>
                        {baseCashFlowColumns.map((column, index) => (
                            <td
                                key={`income-adjustment-period-${column.id}`}
                                className="NSTable__tbody__tr__td"
                                id={`income-adjustment-placeholder-period-${index}`}
                            >
                                <RenderIf isTrue={baseCashFlowColumns.length === 1 && index === 0}>
                                    <UncontrolledTooltip target={`income-adjustment-placeholder-period-${index}`}>
                                        {CASH_FLOW_TOOLTIP_MESSAGE}
                                    </UncontrolledTooltip>
                                </RenderIf>
                            </td>
                        ))}
                    </tr>
                </RenderIf>
            </tbody>
            <tfoot className="NSTable__tfoot">
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                        {footerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__tfoot__tr__th p-1">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </NSTable>
    );
};

export default IncomeAdjustmentsTable;
